import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import { Box, GlobalStyles, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import "leaflet/dist/leaflet.css";
import { ReactNode, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { mediumFont, regularFont } from "../../../../utils/styles";
import astrocartographyStyles from "../Astrocartography.styles";
import { ASTRO_ACCENTS, accentVars } from "../astroTheme";

interface AstroMapStageProps {
  containerRef: RefObject<HTMLDivElement>;
  /** The element taken fullscreen — the stage, so the drawer comes along. */
  stageRef: RefObject<HTMLDivElement>;
  /** Rendered over the map, e.g. the control drawer. */
  children?: ReactNode;
  overlayIcon?: ReactNode;
  overlayTitle?: string;
  overlayMessage?: string;
  /** Swaps the overlay icon for an orbit animation while data loads. */
  isLoading?: boolean;
  isFullscreen: boolean;
  isFullscreenSupported: boolean;
  onToggleFullscreen: () => void;
}

/**
 * Leaflet renders into plain DOM, so the pieces it owns — tooltips, popups,
 * badges, controls, the birth pin — are themed here with global styles rather
 * than `sx`. The one keyframe the panels rely on lives here too.
 */
const mapGlobalStyles = {
  "@keyframes astroOrbit": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  ".leaflet-container": {
    fontFamily: regularFont.fontFamily,
    background: "#0a0818",
  },
  ".leaflet-bar": {
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
  },
  ".leaflet-bar a, .leaflet-bar a:hover": {
    background: "rgba(10, 8, 24, 0.86)",
    backdropFilter: "blur(12px)",
    color: "rgba(255,255,255,0.75)",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    transition: "background 0.2s ease, color 0.2s ease",
  },
  ".leaflet-bar a:hover": {
    background: "rgba(144,83,246,0.35)",
    color: "#FFFFFF",
  },
  ".leaflet-control-attribution": {
    background: "rgba(10, 8, 24, 0.7) !important",
    backdropFilter: "blur(10px)",
    color: "rgba(255,255,255,0.4)",
    borderRadius: "8px 0 0 0",
    padding: "2px 8px",
    fontSize: "10px",
  },
  ".leaflet-control-attribution a": { color: "rgba(255,255,255,0.6)" },
  "@keyframes astroPulse": {
    "0%": { transform: "scale(0.7)", opacity: 0.9 },
    "70%": { transform: "scale(2.1)", opacity: 0 },
    "100%": { transform: "scale(2.1)", opacity: 0 },
  },
  // Birth place: a star sitting inside a slow radar pulse.
  ".astro-birth-pin": {
    position: "relative" as const,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
  },
  ".astro-birth-pulse": {
    position: "absolute" as const,
    inset: "7px",
    borderRadius: "50%",
    border: "2px solid #2CF1DF",
    animation: "astroPulse 2.6s ease-out infinite",
  },
  ".astro-birth-star": {
    position: "relative" as const,
    fontSize: "17px",
    lineHeight: 1,
    color: "#FFFFFF",
    textShadow: "0 0 6px #2CF1DF, 0 0 10px rgba(0,0,0,0.95)",
  },
  // Ranked city hit, numbered to match the results list.
  ".astro-city-pin": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    fontFamily: mediumFont.fontFamily,
    fontSize: "10px",
    fontWeight: 700,
    color: "#FFFFFF",
    background: "var(--astro-city-color)",
    border: "1.5px solid rgba(255,255,255,0.92)",
    boxShadow: "0 0 10px var(--astro-city-color), 0 2px 6px rgba(0,0,0,0.6)",
    fontVariantNumeric: "tabular-nums" as const,
  },
  ".astro-line-tooltip": {
    background: "rgba(10, 8, 24, 0.9)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.14)",
    color: "#FFFFFF",
    fontFamily: mediumFont.fontFamily,
    fontSize: "10px",
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: "7px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.5)",
    whiteSpace: "nowrap" as const,
  },
  ".astro-line-tooltip::before": { display: "none" },
  // Line name, placed where the line crosses the middle of the viewport.
  ".astro-line-label": {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "3px 8px 3px 6px",
    borderRadius: "7px",
    fontFamily: mediumFont.fontFamily,
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.02em",
    lineHeight: 1.35,
    whiteSpace: "nowrap" as const,
    color: "#FFFFFF",
    background: "rgba(8, 6, 20, 0.82)",
    backdropFilter: "blur(6px)",
    border: "1px solid rgba(255,255,255,0.16)",
    borderLeft: "3px solid var(--astro-line-color)",
    // A dark halo keeps the name readable over satellite imagery too.
    textShadow:
      "0 1px 2px rgba(0,0,0,0.95), 0 0 6px rgba(0,0,0,0.85)",
    boxShadow: "0 2px 10px rgba(0,0,0,0.55)",
  },
  ".astro-line-label-dot": {
    width: "6px",
    height: "6px",
    flex: "0 0 6px",
    borderRadius: "50%",
    background: "var(--astro-line-color)",
    boxShadow: "0 0 7px var(--astro-line-color)",
  },
  ".astro-popup-shell .leaflet-popup-content-wrapper": {
    background: "rgba(12, 10, 30, 0.96)",
    backdropFilter: "blur(18px)",
    color: "#E8EBEF",
    border: "1px solid rgba(144, 83, 246, 0.42)",
    borderRadius: "16px",
    boxShadow: "0 18px 50px rgba(0,0,0,0.6)",
  },
  ".astro-popup-shell .leaflet-popup-content": { margin: "14px 16px" },
  ".astro-popup-shell .leaflet-popup-tip": {
    background: "rgba(12, 10, 30, 0.96)",
    border: "1px solid rgba(144, 83, 246, 0.42)",
  },
  ".astro-popup-shell a.leaflet-popup-close-button": {
    color: "rgba(255,255,255,0.5)",
    padding: "8px 8px 0 0",
  },
  ".astro-popup": {
    maxWidth: "300px",
    lineHeight: 1.6,
    fontFamily: regularFont.fontFamily,
    fontSize: "12.5px",
  },
  ".astro-popup p": { margin: "0 0 8px", color: "rgba(255,255,255,0.78)" },
  ".astro-popup-heading": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: mediumFont.fontFamily,
    fontSize: "14px",
    fontWeight: 600,
    color: "#FFFFFF",
    marginBottom: "8px",
    paddingRight: "14px",
  },
  ".astro-popup-swatch": {
    display: "inline-block",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    flex: "0 0 12px",
    boxShadow: "0 0 8px currentColor",
  },
  ".astro-popup-chip": {
    display: "inline-block",
    fontFamily: mediumFont.fontFamily,
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    padding: "2px 9px",
    borderRadius: "10px",
    marginBottom: "9px",
  },
  ".astro-popup-chip.is-harmonious": {
    background: "rgba(16,185,129,.2)",
    color: "#6EE7B7",
    border: "1px solid rgba(16,185,129,.5)",
  },
  ".astro-popup-chip.is-challenging": {
    background: "rgba(244,63,94,.18)",
    color: "#FDA4AF",
    border: "1px solid rgba(244,63,94,.5)",
  },
  ".astro-popup-meta": {
    color: "rgba(255,255,255,0.45)",
    fontSize: "11px",
    marginBottom: "8px",
  },
  ".astro-popup-empty": {
    color: "rgba(255,255,255,0.5)",
    fontStyle: "italic" as const,
  },
  ".astro-popup-note": {
    color: "rgba(255,255,255,0.42)",
    fontSize: "10.5px",
    marginBottom: 0,
    borderTop: "1px solid rgba(255,255,255,0.1)",
    paddingTop: "8px",
  },
};

/** The map surface: an edge-to-edge Leaflet canvas and its state overlay. */
const AstroMapStage = (props: AstroMapStageProps) => {
  const classes = astrocartographyStyles;
  const { t } = useTranslation();
  const {
    containerRef,
    stageRef,
    children,
    overlayIcon,
    overlayTitle,
    overlayMessage,
    isLoading,
    isFullscreen,
    isFullscreenSupported,
    onToggleFullscreen,
  } = props;

  return (
    <Box ref={stageRef} sx={classes.stage}>
      <GlobalStyles styles={mapGlobalStyles} />
      <Box ref={containerRef} sx={classes.mapCanvas} />
      <Box sx={classes.stageGlow} />

      {children}

      {isFullscreenSupported && (
        <Tooltip
          title={isFullscreen ? t("astroExitFullscreen") : t("astroFullscreen")}
          placement="left"
        >
          <IconButton onClick={onToggleFullscreen} sx={classes.stageAction}>
            {isFullscreen ? (
              <FullscreenExitRoundedIcon />
            ) : (
              <FullscreenRoundedIcon />
            )}
          </IconButton>
        </Tooltip>
      )}

      {(overlayTitle || overlayMessage) && (
        <Box sx={{ ...classes.overlay, ...accentVars(ASTRO_ACCENTS.profile) }}>
          <Paper sx={classes.overlayCard} elevation={0}>
            {isLoading ? (
              <Box sx={classes.overlayOrbit}>☉</Box>
            ) : (
              overlayIcon && <Box sx={classes.overlayIcon}>{overlayIcon}</Box>
            )}
            {overlayTitle && (
              <Typography variant="h6" color="text.primary">
                {overlayTitle}
              </Typography>
            )}
            {overlayMessage && (
              <Typography variant="body2" color="text.secondary">
                {overlayMessage}
              </Typography>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default AstroMapStage;
